import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface TokenContextType {
  tokens: number;
  addTokens: (amount: number) => void;
  deductTokens: (amount: number) => boolean;
  hasEnoughTokens: (amount: number) => boolean;
}

const TokenContext = createContext<TokenContextType | undefined>(undefined);

export function TokenProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokens] = useState<number>(() => {
    const stored = localStorage.getItem('userTokens');
    return stored ? parseInt(stored, 10) : 100;
  });

  useEffect(() => {
    localStorage.setItem('userTokens', tokens.toString());
  }, [tokens]);

  const addTokens = (amount: number) => {
    setTokens((prev) => prev + amount);
  };

  const deductTokens = (amount: number): boolean => {
    if (tokens >= amount) {
      setTokens((prev) => prev - amount);
      return true;
    }
    return false;
  };

  const hasEnoughTokens = (amount: number): boolean => {
    return tokens >= amount;
  };

  return (
    <TokenContext.Provider value={{ tokens, addTokens, deductTokens, hasEnoughTokens }}>
      {children}
    </TokenContext.Provider>
  );
}

export function useTokens() {
  const context = useContext(TokenContext);
  if (context === undefined) {
    throw new Error('useTokens must be used within a TokenProvider');
  }
  return context;
}